<template>
    <i :class="iconClass" :style="iconStyle" />
</template>

<script>
/**
 * Drop-in replacement for `<v-icon>` that uses MDI directly without going
 * through Vuetify. Same prop surface (name in default slot or `name` prop,
 * `small/large/x-large/x-small` size flags, `color` token, `left/right`
 * spacing flags) so the Phase 2.1 codemod can be a near-pure string swap.
 *
 * The translation map below covers the Material-Icons → MDI name remaps
 * Vuetify 1.x did internally with `iconfont: 'mdi'`. Names already in MDI
 * form (or starting with `mdi-`) pass through unchanged.
 */

// Material Icons name -> MDI icon (without the `mdi-` prefix). Only entries
// that DIFFER from the Material name are listed; everything else falls
// through to the input name verbatim.
const MATERIAL_TO_MDI = {
    add: 'plus',
    arrow_back: 'arrow-left',
    arrow_forward: 'arrow-right',
    arrow_upward: 'arrow-up',
    arrow_downward: 'arrow-down',
    attach_file: 'paperclip',
    chevron_left: 'chevron-left',
    chevron_right: 'chevron-right',
    chevron_up: 'chevron-up',
    chevron_down: 'chevron-down',
    done: 'check',
    edit: 'pencil',
    filter_list: 'filter-variant',
    fullscreen_exit: 'fullscreen-exit',
    local_phone: 'phone',
    mic: 'microphone',
    mic_off: 'microphone-off',
    open_in_new: 'open-in-new',
    person_add: 'account-plus',
    phone_disabled: 'phone-off',
    portable_wifi_off: 'wifi-off',
    screen_share: 'monitor-share',
    stop_screen_share: 'monitor-off',
    system_update_alt: 'tray-arrow-down',
    unarchive: 'archive-arrow-up',
    videocam: 'video',
    videocam_off: 'video-off',
}

export default {
    name: 'LearnUIcon',
    props: {
        name: { type: String, default: '' },
        small: { type: Boolean, default: false },
        xSmall: { type: Boolean, default: false },
        large: { type: Boolean, default: false },
        xLarge: { type: Boolean, default: false },
        color: { type: String, default: '' },
        left: { type: Boolean, default: false },
        right: { type: Boolean, default: false },
        size: { type: [String, Number], default: '' },
    },
    computed: {
        // Resolve "delete", "mdi-delete", or any user-provided string to the
        // canonical mdi class name (no leading prefix).
        resolvedIconName() {
            const raw = (this.name || '').trim()
            if (!raw) return ''
            if (raw.startsWith('mdi-')) return raw.slice(4)
            // Material Icons names use `_`; MDI uses `-`. Normalise + map.
            const normalised = raw.replace(/_/g, '_')
            if (MATERIAL_TO_MDI[normalised]) return MATERIAL_TO_MDI[normalised]
            return raw.replace(/_/g, '-')
        },
        iconClass() {
            const cls = ['mdi', `mdi-${this.resolvedIconName}`, 'tw-inline-block']
            if (this.small) cls.push('tw-text-base')
            if (this.xSmall) cls.push('tw-text-sm')
            if (this.large) cls.push('tw-text-2xl')
            if (this.xLarge) cls.push('tw-text-3xl')
            if (this.left) cls.push('tw-mr-2')
            if (this.right) cls.push('tw-ml-2')
            // Vuetify color shorthand `<v-icon color="white">` -> tw-text-white.
            // Tokens that already match tailwind names work directly; unknowns
            // fall through (component consumer can override via `class`).
            if (this.color) cls.push(`tw-text-${this.color}`)
            return cls
        },
        iconStyle() {
            // Numeric `size` -> px; string `size` -> raw (e.g. "20px", "1.5rem").
            if (typeof this.size === 'number' && this.size) return { fontSize: `${this.size}px` }
            if (typeof this.size === 'string' && this.size) return { fontSize: this.size }
            return null
        },
    },
}
</script>
